/**
 * IAN Assist — Office Auth Service
 *
 * Authentication strategy:
 *   1. Try SSO via OfficeRuntime.auth.getAccessToken() — requires Azure AD
 *      app registration + <WebApplicationInfo> in manifest.xml.
 *   2. Fallback: use Office.context.mailbox.userProfile to get the
 *      logged-in user's identity (email, displayName) without any
 *      Azure AD setup. The user info is returned so callers can
 *      embed it in the request body.
 *
 * TODO: Enable SSO once an Azure AD App Registration is created.
 *       1. Register app in Azure AD (Entra ID)
 *       2. Expose api://<domain>/<client-id> with access_as_user scope
 *       3. Pre-authorize Office client IDs (d3590ed6-..., ea5a67f6-...)
 *       4. Add <WebApplicationInfo> block to manifest.xml
 */

/* global Office, OfficeRuntime */

let cachedToken = null;
let tokenExpiry = 0;

/**
 * Get an AAD access token via SSO.
 * Returns the token string on success, or null if SSO is not available.
 * @returns {Promise<string|null>}
 */
export async function getAccessToken() {
  const now = Date.now();

  // Return cached token if still valid (5-min buffer)
  if (cachedToken && tokenExpiry > now + 300_000) {
    console.log('[officeAuth] returning cached token (expires in', Math.round((tokenExpiry - now) / 1000), 'sec)');
    return cachedToken;
  }

  try {
    if (typeof OfficeRuntime !== 'undefined' && OfficeRuntime.auth) {
      console.log('[officeAuth] requesting SSO token via OfficeRuntime.auth...');
      const token = await OfficeRuntime.auth.getAccessToken({
        allowSignInPrompt: true,
        allowConsentPrompt: true,
      });

      cachedToken = token;
      tokenExpiry = parseTokenExpiry(token);
      console.log('[officeAuth] SSO token obtained, length:', token.length, 'expires:', new Date(tokenExpiry).toISOString());
      return token;
    } else {
      console.log('[officeAuth] OfficeRuntime.auth not available, SSO skipped');
    }
  } catch (error) {
    console.warn('[officeAuth] SSO failed:', error.code || error.message, '— falling back to userProfile');
    cachedToken = null;
    tokenExpiry = 0;
  }

  console.log('[officeAuth] returning null (no SSO token)');
  return null;
}

/**
 * Get the logged-in user's identity from the Outlook session.
 * Works without any Azure AD app registration.
 * @returns {{ email: string, displayName: string, timeZone: string }}
 */
export function getUserProfile() {
  try {
    const profile = Office.context.mailbox.userProfile;
    const result = {
      email: profile.emailAddress || '',
      displayName: profile.displayName || '',
      timeZone: profile.timeZone || '',
    };
    console.log('[officeAuth] getUserProfile:', result.email || '(no email)', result.displayName || '(no name)');
    return result;
  } catch (err) {
    console.warn('[officeAuth] getUserProfile failed:', err.message || 'Office.context not available');
    return { email: '', displayName: '', timeZone: '' };
  }
}

/**
 * Parse JWT exp claim without a library.
 */
function parseTokenExpiry(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : Date.now() + 3_600_000;
  } catch {
    return Date.now() + 3_600_000; // Default 1hr
  }
}

/**
 * Clear cached token (e.g. on auth failure retry).
 */
export function clearTokenCache() {
  cachedToken = null;
  tokenExpiry = 0;
}
